/**
 * Call View
 * Pure presentational component for the call screen
 */

import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Grid,
  GridItem,
} from "@chakra-ui/react";
import type { CallViewModel } from "../viewModel/useCallViewModel";
import { AssetImage } from "../../../components/AssetImage";

// Icons
const EndCallIcon = () => (
  <svg viewBox="0 0 24 24" width="32" height="32" fill="currentColor">
    <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.17-.29-.42-.29-.7 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.68-1.36-2.66-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z" />
  </svg>
);

const MuteIcon = ({ muted }: { muted: boolean }) => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
    {muted ? (
      <path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z" />
    ) : (
      <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" />
    )}
  </svg>
);

const SpeakerIcon = ({ on }: { on: boolean }) => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
    {on ? (
      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
    ) : (
      <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
    )}
  </svg>
);

const KeypadIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
    <path d="M12 19c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM6 1c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm12-8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm-6 8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm-6 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
  </svg>
);

interface CallViewProps {
  viewModel: CallViewModel;
}

// Format duration as MM:SS
function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}

export function CallView({ viewModel }: CallViewProps) {
  const {
    contact,
    status,
    duration,
    isMuted,
    isSpeakerOn,
    dtmfInput,
    showDtmfPad,
    handleEndCall,
    handleToggleMute,
    handleToggleSpeaker,
    handleDtmfPress,
    handleToggleDtmfPad,
    handleBackToContacts,
  } = viewModel;

  // Get localized contact name
  const contactName =
    contact?.contactName?.en ||
    Object.values(contact?.contactName || {})[0] ||
    "Unknown";

  // No contact - shouldn't happen
  if (!contact) {
    return (
      <Box
        minH="100vh"
        bg="bg"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <VStack gap="4">
          <Text color="fg">No contact selected</Text>
          <Button onClick={handleBackToContacts}>Back to Contacts</Button>
        </VStack>
      </Box>
    );
  }

  // Active call screen
  return (
    <Box
      minH="100vh"
      bg="linear-gradient(180deg, #1a365d 0%, #0d1117 100%)"
      color="white"
      display="flex"
      flexDirection="column"
    >
      <Container
        maxW="sm"
        py="10"
        flex="1"
        display="flex"
        flexDirection="column"
      >
        {/* Contact Info */}
        <VStack gap="2" pt="10" flex="1">
          {/* Avatar */}
          <Box
            w="28"
            h="28"
            borderRadius="full"
            bg={contact.contactIcon ? "transparent" : "whiteAlpha.200"}
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontSize="4xl"
            fontWeight="bold"
            overflow="hidden"
          >
            {contact.contactIcon ? (
              <AssetImage assetId={contact.contactIcon} alt={contactName} />
            ) : (
              contactName.charAt(0).toUpperCase()
            )}
          </Box>

          {/* Name */}
          <Heading as="h1" size="xl" textAlign="center" mt="4">
            {contactName}
          </Heading>

          {/* Status */}
          <Text color="whiteAlpha.700" fontSize="lg">
            {status === "ringing" && "Calling..."}
            {status === "connecting" && "Connecting..."}
            {status === "in-call" && formatDuration(duration)}
            {status === "failed" && "Call Failed"}
          </Text>

          {/* DTMF Input Display */}
          {dtmfInput && (
            <Text fontSize="2xl" fontFamily="mono" mt="4" letterSpacing="0.2em">
              {dtmfInput}
            </Text>
          )}
        </VStack>

        {/* DTMF Keypad */}
        {showDtmfPad && (
          <Box bg="whiteAlpha.100" borderRadius="2xl" p="4" mb="6">
            <Grid templateColumns="repeat(3, 1fr)" gap="3">
              {["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"].map(
                (digit) => (
                  <GridItem key={digit}>
                    <Button
                      w="full"
                      h="16"
                      fontSize="2xl"
                      variant="ghost"
                      color="white"
                      _hover={{ bg: "whiteAlpha.200" }}
                      onClick={() => handleDtmfPress(digit)}
                    >
                      {digit}
                    </Button>
                  </GridItem>
                ),
              )}
            </Grid>
          </Box>
        )}

        {/* Call Controls */}
        <VStack gap="6" pb="10">
          {/* Secondary Controls */}
          <HStack gap="8" justify="center">
            <VStack gap="1">
              <Button
                w="14"
                h="14"
                borderRadius="full"
                bg={isMuted ? "white" : "whiteAlpha.200"}
                color={isMuted ? "gray.800" : "white"}
                _hover={{ bg: isMuted ? "gray.200" : "whiteAlpha.300" }}
                onClick={handleToggleMute}
              >
                <MuteIcon muted={isMuted} />
              </Button>
              <Text fontSize="xs" color="whiteAlpha.700">
                Mute
              </Text>
            </VStack>

            <VStack gap="1">
              <Button
                w="14"
                h="14"
                borderRadius="full"
                bg={showDtmfPad ? "white" : "whiteAlpha.200"}
                color={showDtmfPad ? "gray.800" : "white"}
                _hover={{ bg: showDtmfPad ? "gray.200" : "whiteAlpha.300" }}
                onClick={handleToggleDtmfPad}
              >
                <KeypadIcon />
              </Button>
              <Text fontSize="xs" color="whiteAlpha.700">
                Keypad
              </Text>
            </VStack>

            <VStack gap="1">
              <Button
                w="14"
                h="14"
                borderRadius="full"
                bg={isSpeakerOn ? "white" : "whiteAlpha.200"}
                color={isSpeakerOn ? "gray.800" : "white"}
                _hover={{ bg: isSpeakerOn ? "gray.200" : "whiteAlpha.300" }}
                onClick={handleToggleSpeaker}
              >
                <SpeakerIcon on={isSpeakerOn} />
              </Button>
              <Text fontSize="xs" color="whiteAlpha.700">
                Speaker
              </Text>
            </VStack>
          </HStack>

          {/* End Call Button */}
          <Button
            w="20"
            h="20"
            borderRadius="full"
            bg="red.500"
            color="white"
            _hover={{ bg: "red.600" }}
            onClick={handleEndCall}
          >
            <EndCallIcon />
          </Button>
        </VStack>
      </Container>
    </Box>
  );
}
